import React from 'react';
import { Avatar, Box, SxProps, Theme } from '@mui/material';
import { tokens, personColor, PREMIUM_COLORS } from '../../theme';
import { initials as makeInitials } from '../../utils/format';

export type PillTone = 'success' | 'warning' | 'danger' | 'info' | 'plum' | 'neutral' | 'champagne';

const TONES: Record<PillTone, { bg: string; fg: string; dot: string }> = {
  success: { bg: tokens.successSoft, fg: tokens.success, dot: '#3A9A6F' },
  warning: { bg: tokens.warningSoft, fg: tokens.warningText, dot: tokens.warning },
  danger: { bg: tokens.dangerSoft, fg: tokens.danger, dot: '#C4554F' },
  info: { bg: tokens.infoSoft, fg: tokens.info, dot: tokens.info },
  plum: { bg: tokens.plumSoft, fg: tokens.plum, dot: '#6F5BC4' },
  neutral: { bg: tokens.surfaceStrong, fg: tokens.inkSoft, dot: '#8E859E' },
  champagne: { bg: tokens.champagneSoft, fg: tokens.champagneDeep, dot: tokens.champagne },
};

export const toneColors = (tone: PillTone) => TONES[tone];

interface StatusPillProps {
  label: React.ReactNode;
  tone?: PillTone;
  /** Bez kropki, np. dla etykiet kategorii. */
  noDot?: boolean;
  size?: 'small' | 'medium';
  sx?: SxProps<Theme>;
}

/** Status jako spokojna etykieta z kropka. */
export const StatusPill: React.FC<StatusPillProps> = ({ label, tone = 'neutral', noDot, size = 'medium', sx }) => {
  const c = TONES[tone];
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: size === 'small' ? 0.9 : 1.1,
        py: size === 'small' ? 0.15 : 0.35,
        borderRadius: 999,
        bgcolor: c.bg,
        color: c.fg,
        fontSize: size === 'small' ? '0.75rem' : '0.8125rem',
        fontWeight: 650,
        lineHeight: 1.5,
        whiteSpace: 'nowrap',
        ...((sx as object) || {}),
      }}
    >
      {!noDot && <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: c.dot, flexShrink: 0 }} />}
      {label}
    </Box>
  );
};

export const BOOKING_STATUS: Record<string, { label: string; tone: PillTone }> = {
  PENDING: { label: 'Oczekuje', tone: 'warning' },
  CONFIRMED: { label: 'Potwierdzona', tone: 'success' },
  IN_PROGRESS: { label: 'W trakcie', tone: 'plum' },
  COMPLETED: { label: 'Zakończona', tone: 'neutral' },
  CANCELLED: { label: 'Anulowana', tone: 'danger' },
  NO_SHOW: { label: 'Klient nie przyszedł', tone: 'danger' },
};

export const BookingStatusPill: React.FC<{ status?: string | null; size?: 'small' | 'medium'; sx?: SxProps<Theme> }> = ({ status, size, sx }) => {
  const s = BOOKING_STATUS[status || ''] || { label: status || '—', tone: 'neutral' as PillTone };
  return <StatusPill label={s.label} tone={s.tone} size={size} sx={sx} />;
};

/** Kolory statusu wizyty dla elementow graficznych (kalendarz). */
export const bookingStatusColors = PREMIUM_COLORS.status;

interface PersonAvatarProps {
  firstName?: string | null;
  lastName?: string | null;
  /** Tekst zastepczy (np. e-mail), gdy brak imienia. */
  fallback?: string | null;
  /** Staly kolor dla tej samej osoby — zwykle id. */
  seed?: string | number | null;
  src?: string | null;
  size?: number;
  sx?: SxProps<Theme>;
}

/** Awatar osoby z inicjalami i stalym, stonowanym kolorem. */
export const PersonAvatar: React.FC<PersonAvatarProps> = ({ firstName, lastName, fallback, seed, src, size = 36, sx }) => (
  <Avatar
    src={src || undefined}
    sx={{
      width: size,
      height: size,
      fontSize: Math.max(11, Math.round(size * 0.36)),
      fontWeight: 700,
      bgcolor: personColor(seed ?? `${firstName}${lastName}${fallback}`),
      color: '#FFFFFF',
      ...((sx as object) || {}),
    }}
  >
    {makeInitials(firstName, lastName, fallback)}
  </Avatar>
);

export default StatusPill;
